import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React from 'react';
import { formatPhone } from '@rad/utility/formatting';
import { getJsonAbortable } from '../../utility/request';
import IconButton from './button.icon';
const filterTypes = [
    { 'value': 'amount_paid', 'label': 'Amount Paid' },
    { 'value': 'last_four', 'label': 'Last Four of Card' },
    { 'value': 'invoice_number', 'label': 'Invoice Number' },
    { 'value': 'estimate_number', 'label': 'Estimate Number' },
    { 'value': 'part_name', 'label': 'Part Name' },
    { 'value': 'part_number', 'label': 'Part Number' },
    { 'value': 'repair_order_number', 'label': 'Repair Order Number' },
    { 'value': 'po_number', 'label': 'PO Number' },
    { 'value': 'customer', 'label': 'Customers' },
    { 'value': 'phone', 'label': 'Phone Number' },
    { 'value': 'vin_plate', 'label': 'VIN or Plate Number' },
];
const badgeClass = (resultType) => ({
    'invoice': 'bg-primary',
    'customer': 'bg-success',
    'vehicle': 'bg-info',
    'payment': 'bg-warning',
    'part': 'bg-secondary',
}[resultType] || 'bg-secondary');
const applyCustomerSuppression = (results, scoped, filterValues) => {
    if (scoped) {
        return results.filter((x) => x.result_type !== 'customer');
    }
    if (filterValues.length === 0 && results.some((x) => x.result_type === 'customer')) {
        return results.filter((x) => x.result_type === 'customer');
    }
    return results;
};
export default (props) => {
    const containerRef = React.useRef(null);
    const [value, setValue] = React.useState('');
    const [filterValues, setFilterValues] = React.useState([]);
    const [showFilters, setShowFilters] = React.useState(false);
    const [showResults, setShowResults] = React.useState(false);
    const [loading, setLoading] = React.useState(false);
    const [results, setResults] = React.useState([]);
    const [amountMin, setAmountMin] = React.useState('');
    const [amountMax, setAmountMax] = React.useState('');
    const controllerRef = React.useRef(null);
    React.useEffect(() => {
        const onDocumentClick = (event) => {
            if (containerRef.current && !containerRef.current.contains(event.target)) {
                setShowResults(false);
                setShowFilters(false);
            }
        };
        document.addEventListener('click', onDocumentClick);
        return () => document.removeEventListener('click', onDocumentClick);
    }, []);
    React.useEffect(() => {
        if (controllerRef.current) {
            controllerRef.current.abort();
        }
        if (value.length < 3) {
            setResults([]);
            setLoading(false);
            return;
        }
        const controller = new AbortController();
        controllerRef.current = controller;
        let active = true;
        setLoading(true);
        let url = `/global-search?s=${encodeURIComponent(value)}`;
        if (props.clientId) {
            url += `&client_id=${encodeURIComponent(props.clientId)}`;
        }
        if (filterValues.length > 0) {
            url += `&filter=${filterValues.map((f) => encodeURIComponent(f)).join(',')}`;
        }
        if (filterValues.includes('amount_paid')) {
            if (amountMin !== '') {
                url += `&amount_min=${encodeURIComponent(amountMin)}`;
            }
            if (amountMax !== '') {
                url += `&amount_max=${encodeURIComponent(amountMax)}`;
            }
        }
        getJsonAbortable(url, controller)
            .then((x) => {
            if (active) {
                setResults(x || []);
            }
        })
            .catch((e) => {
            if ((e === null || e === void 0 ? void 0 : e.name) !== 'AbortError' && active) {
                setResults([]);
            }
        })
            .finally(() => {
            if (active) {
                setLoading(false);
            }
        });
        return () => {
            active = false;
            controller.abort();
        };
    }, [value, filterValues, amountMin, amountMax, props.clientId]);
    const allSelected = React.useMemo(() => {
        const available = filterTypes.map((f) => f.value);
        return available.length > 0 && available.every((x) => filterValues.includes(x));
    }, [filterValues]);
    const toggleFilter = (filterValue) => {
        setShowResults(false);
        setFilterValues((prev) => {
            if (prev.includes(filterValue)) {
                if (filterValue === 'amount_paid') {
                    setAmountMin('');
                    setAmountMax('');
                }
                return prev.filter((x) => x !== filterValue);
            }
            return [...prev, filterValue];
        });
    };
    const toggleSelectAll = () => {
        setShowResults(false);
        if (allSelected) {
            setFilterValues([]);
            setAmountMin('');
            setAmountMax('');
        }
        else {
            setFilterValues(filterTypes.map((f) => f.value));
        }
    };
    const clearFilters = () => {
        setShowResults(false);
        setFilterValues([]);
        setAmountMin('');
        setAmountMax('');
    };
    const clearSearch = () => {
        setValue('');
        setResults([]);
        setShowResults(false);
        setShowFilters(false);
    };
    const scoped = !!props.clientId;
    const visibleResults = applyCustomerSuppression(results, scoped, filterValues);
    const checkboxIdPrefix = scoped ? `filter-${props.clientId}` : 'filter';
    const selectAllId = scoped ? `select-all-filters-${props.clientId}` : 'select-all-filters';
    return _jsxs("div", { ref: containerRef, className: `position-relative d-flex ${props.wrapperClassName || 'w-100'}`, children: [_jsx(IconButton, { color: props.iconButtonColor || 'primary', muted: filterValues.length === 0 ? true : undefined, title: "Advanced Filters", onClick: () => { setShowResults(false); setShowFilters((x) => !x); }, children: "tune" }), _jsx("input", { type: "text", className: `form-control form-control-sm ms-1 flex-grow-1 ${props.inputClassName || ''}`, value: value, placeholder: props.placeholder || 'Search...', style: props.inputClassName ? undefined : { 'paddingRight': '2.3rem', 'minWidth': 0, 'flexBasis': 0 }, onChange: (e) => { setShowFilters(false); setShowResults(true); setValue(e.currentTarget.value); }, onClick: () => { setShowFilters(false); setShowResults(true); }, onFocus: () => { setShowFilters(false); setShowResults(true); } }), value.length > 0
                ? _jsx("i", { className: `material-symbols-outlined ${props.iconClassName || ''}`, role: "button", "aria-label": "Clear search", title: "Clear search", onClick: clearSearch, style: props.iconClassName ? { 'cursor': 'pointer', 'pointerEvents': 'auto' } : { 'position': 'absolute', 'right': '0.9rem', 'top': '0.4rem', 'fontSize': '1.1rem', 'color': '#6c757d', 'cursor': 'pointer' }, children: "close" })
                : _jsx("i", { className: `material-symbols-outlined ${props.iconClassName || ''}`, style: props.iconClassName ? undefined : { 'position': 'absolute', 'right': '0.9rem', 'top': '0.4rem', 'pointerEvents': 'none', 'fontSize': '1.1rem', 'color': '#6c757d' }, children: "search" }), showResults && _jsxs("div", { className: `position-absolute top-100 start-0 w-100 bg-white border rounded shadow-lg mt-1 text-dark ${props.resultsClassName || ''}`, style: { 'maxHeight': '400px', 'overflowY': 'auto', 'zIndex': 1002 }, children: [loading && _jsx("div", { className: "p-3 text-center text-muted", children: "Loading..." }), !loading && value.length < 3 && _jsx("div", { className: "p-3 text-center text-muted", children: "Type at least 3 characters to search" }), !loading && value.length >= 3 && results.length === 0 && filterValues.length === 0 && _jsxs("div", { className: "p-3 text-center text-muted", children: ["No results found.", ' ', _jsx("a", { href: "#", onClick: (e) => { e.preventDefault(); clearSearch(); }, children: "Clear search" })] }), !loading && value.length >= 3 && results.length === 0 && filterValues.length > 0 && _jsxs("div", { className: "p-3 text-center text-muted", children: ["No results from current filter settings.", ' ', _jsx("a", { href: "#", onClick: (e) => { e.preventDefault(); clearFilters(); }, children: "Clear filters and search again?" })] }), !loading && visibleResults.map((result) => _jsxs("a", { href: `#${result.link}`, target: scoped ? '_blank' : undefined, rel: scoped ? 'noopener noreferrer' : undefined, className: "d-block p-3 border-bottom text-decoration-none text-dark", onClick: (e) => {
                            setValue('');
                            setShowResults(false);
                            if (props.onNavigate && !scoped) {
                                props.onNavigate(e);
                            }
                        }, children: [_jsx("div", { className: "fw-bold", children: result.title }), _jsx("div", { className: "small text-muted", children: result.subtitle }), !!result.match_info && _jsx(_Fragment, { children: _jsx("div", { className: "small text-primary mt-1", children: result.match_kind === 'client-name' ? `Name: ${result.match_info}` :
                                        result.match_kind === 'client-phone' ? `Phone: ${formatPhone(result.match_info)}` :
                                            result.match_info }) }), _jsx("div", { className: "small mt-1", children: _jsx("span", { className: `badge ${badgeClass(result.result_type)}`, children: result.result_type }) })] }, result.id))] }), showFilters && _jsxs("div", { className: `position-absolute top-100 start-0 bg-white border rounded shadow-lg mt-1 p-3 text-dark ${props.filtersClassName || ''}`, style: { 'minWidth': '450px', 'maxHeight': '400px', 'overflowY': 'auto', 'zIndex': 1003 }, onClick: (e) => e.stopPropagation(), children: [_jsxs("div", { className: "d-flex justify-content-between align-items-center mb-2", children: [_jsx("div", { className: "small fw-bold", children: "Filter by Type" }), _jsx("a", { href: "#", className: "small text-decoration-underline", onClick: (e) => { e.preventDefault(); clearFilters(); }, children: "Clear" })] }), _jsxs("div", { className: "form-check mb-2", children: [_jsx("input", { className: "form-check-input", type: "checkbox", id: selectAllId, checked: allSelected, onChange: toggleSelectAll }), _jsx("label", { className: "form-check-label", htmlFor: selectAllId, children: "Select All" })] }), filterTypes.filter((f) => !scoped || (f.value !== 'customer' && f.value !== 'phone')).map((filter) => _jsxs("div", { className: "form-check mb-2", children: [_jsx("input", { className: "form-check-input", type: "checkbox", id: `${checkboxIdPrefix}-${filter.value}`, checked: filterValues.includes(filter.value), onChange: () => toggleFilter(filter.value) }), _jsx("label", { className: "form-check-label", htmlFor: `${checkboxIdPrefix}-${filter.value}`, children: filter.label }), filter.value === 'amount_paid' && filterValues.includes('amount_paid') && _jsxs("div", { className: "mt-2 ms-4 d-flex gap-2 align-items-end", children: [_jsxs("div", { className: "flex-grow-1", children: [_jsx("label", { className: "form-label small mb-1", children: "Min" }), _jsx("input", { type: "number", className: "form-control form-control-sm", value: amountMin, onChange: (e) => setAmountMin(e.currentTarget.value), placeholder: "0.00", step: "0.01", min: "0" })] }), _jsxs("div", { className: "flex-grow-1", children: [_jsx("label", { className: "form-label small mb-1", children: "Max" }), _jsx("input", { type: "number", className: "form-control form-control-sm", value: amountMax, onChange: (e) => setAmountMax(e.currentTarget.value), placeholder: "999999.99", step: "0.01", min: "0" })] })] })] }, filter.value))] })] });
};
